// Professional Calculator - Financial Calculation Engine
// Dynamic calculations for freelancer pricing intelligence

export type TaxRegime = 'mei' | 'simples' | 'pj' | 'clt_simulated'

export interface CalculatorInputs {
  monthlyGoal: number
  weeklyWorkHours: number
  weeksPerMonth: number
  taxPercentage: number
  vacationPercentage: number
  thirteenthPercentage: number
  fixedMonthlyCosts: number
  taxRegime: TaxRegime
}

export interface CalculatorResult {
  // Core calculations
  availableHoursPerMonth: number
  totalMonthlyCost: number
  grossRevenueNeeded: number
  idealHourlyRate: number
  netMonthlyIncome: number
  
  // Breakdown
  taxAmount: number
  vacationReserve: number
  thirteenthReserve: number
  totalDeductions: number
  deductionPercentage: number
}

export interface ProjectSimulationInput {
  type: 'hourly' | 'fixed'
  estimatedHours: number
  fixedPrice?: number
  idealHourlyRate: number
}

export interface ProjectSimulationResult {
  totalValue: number
  effectiveHourlyRate: number
  minimumRecommended: number
  estimatedMargin: number
  marginPercentage: number
  isAboveMinimum: boolean
}

export interface CLTvsPJInput {
  desiredCLTSalary: number
  estimatedBenefits: number
  pjTaxPercentage: number
  pjFixedCosts: number
}

export interface CLTvsPJResult {
  cltTotalCost: number
  cltNetIncome: number
  pjGrossNeeded: number
  pjNetIncome: number
  differencePercentage: number
  pjMinimumMonthly: number
}

// ============================================
// Core Calculator Functions
// ============================================

/**
 * Calculate the ideal hourly rate based on financial goals
 */
export function calculateIdealHourlyRate(inputs: CalculatorInputs): CalculatorResult {
  const {
    monthlyGoal,
    weeklyWorkHours,
    weeksPerMonth,
    taxPercentage,
    vacationPercentage,
    thirteenthPercentage,
    fixedMonthlyCosts
  } = inputs

  // Hours available per month
  const availableHoursPerMonth = weeklyWorkHours * weeksPerMonth

  // Total monthly costs (goal + fixed costs)
  const totalMonthlyCost = monthlyGoal + fixedMonthlyCosts

  // Total deduction percentage (convert from 0-100 to decimal)
  const taxDecimal = taxPercentage / 100
  const vacationDecimal = vacationPercentage / 100
  const thirteenthDecimal = thirteenthPercentage / 100
  const totalDeductionPercentage = taxDecimal + vacationDecimal + thirteenthDecimal

  // Prevent division issues
  if (totalDeductionPercentage >= 1) {
    return {
      availableHoursPerMonth,
      totalMonthlyCost,
      grossRevenueNeeded: 0,
      idealHourlyRate: 0,
      netMonthlyIncome: monthlyGoal,
      taxAmount: 0,
      vacationReserve: 0,
      thirteenthReserve: 0,
      totalDeductions: 0,
      deductionPercentage: totalDeductionPercentage * 100
    }
  }

  // Gross revenue needed to achieve net goal after deductions
  // Formula: net = gross * (1 - deductions), so gross = net / (1 - deductions)
  const grossRevenueNeeded = totalMonthlyCost / (1 - totalDeductionPercentage)

  // Ideal hourly rate
  const idealHourlyRate = availableHoursPerMonth > 0 
    ? grossRevenueNeeded / availableHoursPerMonth 
    : 0

  // Calculate individual amounts
  const taxAmount = grossRevenueNeeded * taxDecimal
  const vacationReserve = grossRevenueNeeded * vacationDecimal
  const thirteenthReserve = grossRevenueNeeded * thirteenthDecimal
  const totalDeductions = taxAmount + vacationReserve + thirteenthReserve

  return {
    availableHoursPerMonth,
    totalMonthlyCost,
    grossRevenueNeeded,
    idealHourlyRate,
    netMonthlyIncome: grossRevenueNeeded - totalDeductions,
    taxAmount,
    vacationReserve,
    thirteenthReserve,
    totalDeductions,
    deductionPercentage: totalDeductionPercentage * 100
  }
}

// ============================================
// Project Simulation Functions
// ============================================

/**
 * Simulate project pricing based on ideal hourly rate
 */
export function simulateProject(input: ProjectSimulationInput): ProjectSimulationResult {
  const { type, estimatedHours, fixedPrice, idealHourlyRate } = input

  let totalValue: number
  let effectiveHourlyRate: number

  if (type === 'hourly') {
    // Hourly project: calculate total based on ideal rate
    totalValue = estimatedHours * idealHourlyRate
    effectiveHourlyRate = idealHourlyRate
  } else {
    // Fixed project: calculate effective hourly rate
    totalValue = fixedPrice || 0
    effectiveHourlyRate = estimatedHours > 0 ? totalValue / estimatedHours : 0
  }

  // Minimum recommended value (based on ideal hourly rate)
  const minimumRecommended = estimatedHours * idealHourlyRate

  // Margin calculation
  const estimatedMargin = totalValue - minimumRecommended
  const marginPercentage = minimumRecommended > 0 
    ? ((totalValue - minimumRecommended) / minimumRecommended) * 100 
    : 0

  return {
    totalValue,
    effectiveHourlyRate,
    minimumRecommended,
    estimatedMargin,
    marginPercentage,
    isAboveMinimum: totalValue >= minimumRecommended
  }
}

// ============================================
// CLT vs PJ Comparison Functions
// ============================================

/**
 * Compare CLT vs PJ income scenarios
 */
export function compareCLTvsPJ(input: CLTvsPJInput): CLTvsPJResult {
  const { desiredCLTSalary, estimatedBenefits, pjTaxPercentage, pjFixedCosts } = input

  // CLT calculations
  // Approximate CLT deductions (INSS + IR simplified)
  const cltDeductionRate = 0.275 // ~27.5% average for deductions
  const cltNetIncome = desiredCLTSalary * (1 - cltDeductionRate)
  const cltTotalCost = desiredCLTSalary + estimatedBenefits

  // PJ calculations
  // To have the same net income as CLT, what gross PJ do I need?
  const pjTaxDecimal = pjTaxPercentage / 100
  
  // Net = Gross * (1 - tax) - fixedCosts
  // So: Gross = (Net + fixedCosts) / (1 - tax)
  const targetNetWithCosts = cltNetIncome + pjFixedCosts
  const pjGrossNeeded = targetNetWithCosts / (1 - pjTaxDecimal)
  const pjNetIncome = pjGrossNeeded * (1 - pjTaxDecimal) - pjFixedCosts

  // Difference percentage
  const differencePercentage = cltNetIncome > 0 
    ? ((pjGrossNeeded - desiredCLTSalary) / desiredCLTSalary) * 100 
    : 0

  return {
    cltTotalCost,
    cltNetIncome,
    pjGrossNeeded,
    pjNetIncome,
    differencePercentage,
    pjMinimumMonthly: pjGrossNeeded
  }
}

// ============================================
// Tax Regime Presets
// ============================================

export interface TaxRegimePreset {
  taxPercentage: number
  vacationPercentage: number
  thirteenthPercentage: number
  description: string
}

export const TAX_REGIME_PRESETS: Record<TaxRegime, TaxRegimePreset> = {
  mei: {
    taxPercentage: 5,
    vacationPercentage: 8.33,
    thirteenthPercentage: 8.33,
    description: 'Microempreendedor Individual - Limite R$ 81.000/ano'
  },
  simples: {
    taxPercentage: 15.5,
    vacationPercentage: 8.33,
    thirteenthPercentage: 8.33,
    description: 'Simples Nacional - Alíquota média para serviços'
  },
  pj: {
    taxPercentage: 16,
    vacationPercentage: 8.33,
    thirteenthPercentage: 8.33,
    description: 'PJ Lucro Presumido - Alíquota estimada'
  },
  clt_simulated: {
    taxPercentage: 27.5,
    vacationPercentage: 0,
    thirteenthPercentage: 0,
    description: 'Simulação de equivalência CLT'
  }
}

// ============================================
// Formatting Utilities
// ============================================

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`
}
